'use client';

/**
 * Stripe Payment Element for the cafe ordering site
 * (docs/payments-consumer-cafe-plan.md §3, §5).
 *
 * Deferred-intent + manual payment-method creation: the element is mounted
 * with the cart amount, the customer's card (or Apple Pay / Google Pay wallet,
 * which the element surfaces on its own) becomes a PaymentMethod id, and the
 * server charges it. The client never sees or sets the charged amount — the
 * amount here is display/validation only, the server re-prices the cart.
 *
 * Uses stripe-js directly rather than @stripe/react-stripe-js so the site
 * ships no extra dependency.
 */

import { useCallback, useEffect, useImperativeHandle, useRef, useState, forwardRef } from 'react';
import { loadStripe, type Stripe, type StripeElements } from '@stripe/stripe-js';

let stripePromise: Promise<Stripe | null> | null = null;
function getStripe(): Promise<Stripe | null> {
  const key = process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY;
  if (!key) return Promise.resolve(null);
  if (!stripePromise) stripePromise = loadStripe(key);
  return stripePromise;
}

/** True when the site can take cards at all (key present + seller onboarded). */
export function paymentsAvailable(sellerAcceptsCards: boolean): boolean {
  return Boolean(process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY) && sellerAcceptsCards;
}

export interface PaymentFormHandle {
  /** Validate + tokenize. Returns a PaymentMethod id, or null if not ready. */
  createPaymentMethod: () => Promise<string | null>;
  /** Finish a 3DS challenge after the server says the charge needs action. */
  handleNextAction: (clientSecret: string) => Promise<boolean>;
}

export const PaymentForm = forwardRef<
  PaymentFormHandle,
  { amountCents: number; theme: { bg: string; ink: string; primary: string; radius: number }; onError: (m: string) => void }
>(function PaymentForm({ amountCents, theme, onError }, ref) {
  const hostRef = useRef<HTMLDivElement>(null);
  const stripeRef = useRef<Stripe | null>(null);
  const elementsRef = useRef<StripeElements | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const stripe = await getStripe();
      if (!stripe || cancelled || !hostRef.current) return;
      stripeRef.current = stripe;
      const elements = stripe.elements({
        mode: 'payment',
        amount: Math.max(50, amountCents),
        currency: 'usd',
        paymentMethodCreation: 'manual',
        appearance: {
          theme: 'stripe',
          variables: {
            colorBackground: theme.bg,
            colorText: theme.ink,
            colorPrimary: theme.primary,
            borderRadius: `${theme.radius}px`,
            fontFamily: 'var(--font-site-body), system-ui, sans-serif',
          },
        },
      });
      elementsRef.current = elements;
      const el = elements.create('payment', { layout: 'tabs' });
      el.mount(hostRef.current);
      el.on('ready', () => !cancelled && setReady(true));
    })();
    return () => {
      cancelled = true;
      elementsRef.current = null;
    };
    // Amount is fixed for the life of the sheet (the cart is frozen at checkout).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createPaymentMethod = useCallback(async () => {
    const stripe = stripeRef.current;
    const elements = elementsRef.current;
    if (!stripe || !elements) return null;
    const submit = await elements.submit();
    if (submit.error) {
      onError(submit.error.message ?? 'Please check your card details.');
      return null;
    }
    const { error, paymentMethod } = await stripe.createPaymentMethod({ elements });
    if (error || !paymentMethod) {
      onError(error?.message ?? 'Could not read the payment method.');
      return null;
    }
    return paymentMethod.id;
  }, [onError]);

  const handleNextAction = useCallback(
    async (clientSecret: string) => {
      const stripe = stripeRef.current;
      if (!stripe) return false;
      const { error } = await stripe.handleNextAction({ clientSecret });
      if (error) {
        onError(error.message ?? 'Card authentication failed.');
        return false;
      }
      return true;
    },
    [onError],
  );

  useImperativeHandle(ref, () => ({ createPaymentMethod, handleNextAction }), [createPaymentMethod, handleNextAction]);

  return (
    <div>
      <div ref={hostRef} />
      {!ready && <p className="py-3 text-center text-xs text-[var(--s-muted)]">Loading payment…</p>}
    </div>
  );
});
